// Pure logic for the count sheet (kitchen flow, Stage 3). Kept out of the
// component so the rules are pinned by tests.
import type { CountSessionLine } from './visibilityTypes'

export const COUNT_EPS = 1e-4

/** counted − expected, 0 when within rounding. Mirrors countVariance in Go. */
export function countVariance(counted: number, expected: number): number {
  const v = counted - expected
  return Math.abs(v) < COUNT_EPS ? 0 : Math.round(v * 1e5) / 1e5
}

export interface ShelfGroup {
  category: string
  lines: CountSessionLine[]
}

/** Lines grouped by category in shelf order (the server's position order). */
export function groupByShelf(lines: CountSessionLine[], uncategorised = 'Other'): ShelfGroup[] {
  const sorted = [...lines].sort((a, b) => a.position - b.position || a.item_name.localeCompare(b.item_name))
  const groups: ShelfGroup[] = []
  for (const l of sorted) {
    const cat = l.category_name ?? uncategorised
    const last = groups[groups.length - 1]
    if (last && last.category === cat) last.lines.push(l)
    else groups.push({ category: cat, lines: [l] })
  }
  return groups
}

/**
 * The figure a line shows: an unsaved draft wins over what the server holds.
 * `drafts` maps item id → typed qty (null = cleared).
 */
export function shownCount(line: CountSessionLine, drafts: Record<string, number | null>): number | null {
  return line.stock_item_id in drafts ? drafts[line.stock_item_id] : line.counted_qty
}

/**
 * The book figure a line shows, or null while the book is hidden (the count
 * is blind until it is sent, and then only the approver sees it). Before a
 * line is saved the server has no expected figure, so the live balance
 * stands in for it.
 */
export function shownBook(line: CountSessionLine, drafts: Record<string, number | null>): number | null {
  if (line.stock_item_id in drafts || line.expected_qty == null) return line.current_qty
  return line.expected_qty
}

/** The variance to show — null when not counted or while the book is hidden. */
export function shownVariance(line: CountSessionLine, drafts: Record<string, number | null>): number | null {
  const c = shownCount(line, drafts)
  const book = shownBook(line, drafts)
  if (c == null || book == null) return null
  return countVariance(c, book)
}

/** Drafts that differ from what the server holds — what Save would send. */
export function pendingCounts(lines: CountSessionLine[], drafts: Record<string, number | null>): { stock_item_id: string; counted_qty: number | null }[] {
  const byId = new Map(lines.map((l) => [l.stock_item_id, l]))
  const out: { stock_item_id: string; counted_qty: number | null }[] = []
  for (const [id, qty] of Object.entries(drafts)) {
    const l = byId.get(id)
    if (l && l.counted_qty === qty) continue
    out.push({ stock_item_id: id, counted_qty: qty })
  }
  return out
}

/**
 * The pending saves with how long ago each line was entered, so a save that
 * reaches the server late keeps the moment the shelf was counted.
 * `enteredAt` maps item id → Date.now() when typed.
 */
export function withCountedAge(
  pending: { stock_item_id: string; counted_qty: number | null }[],
  enteredAt: Record<string, number>,
  now: number,
): { stock_item_id: string; counted_qty: number | null; counted_seconds_ago?: number }[] {
  return pending.map((p) => {
    const at = enteredAt[p.stock_item_id]
    if (p.counted_qty == null || at == null || !(now > at)) return p
    return { ...p, counted_seconds_ago: Math.round((now - at) / 100) / 10 }
  })
}

/**
 * After a save: drop the drafts the server now holds, keep any typed again
 * since (their value differs from what was sent).
 */
export function settleDrafts(
  drafts: Record<string, number | null>,
  sent: { stock_item_id: string; counted_qty: number | null }[],
): Record<string, number | null> {
  const next = { ...drafts }
  for (const s of sent) {
    if (s.stock_item_id in next && next[s.stock_item_id] === s.counted_qty) delete next[s.stock_item_id]
  }
  return next
}

/** Unsaved counts kept on the device (per count), so a refresh or a sleeping
 *  tablet never loses them; the server copy wins once saved. */
export interface StoredCountDrafts {
  drafts: Record<string, number | null>
  enteredAt: Record<string, number>
}

const draftKey = (sessionId: string) => `inventory-flow:count-drafts:${sessionId}`

export function loadCountDrafts(sessionId: string): StoredCountDrafts {
  try {
    const raw = window.localStorage.getItem(draftKey(sessionId))
    if (!raw) return { drafts: {}, enteredAt: {} }
    const v = JSON.parse(raw) as Partial<StoredCountDrafts>
    return { drafts: v.drafts ?? {}, enteredAt: v.enteredAt ?? {} }
  } catch {
    return { drafts: {}, enteredAt: {} }
  }
}

export function storeCountDrafts(sessionId: string, v: StoredCountDrafts): void {
  try {
    if (Object.keys(v.drafts).length === 0) window.localStorage.removeItem(draftKey(sessionId))
    else window.localStorage.setItem(draftKey(sessionId), JSON.stringify(v))
  } catch {
    // Storage blocked or full: the autosave still keeps counts on the server.
  }
}

export function progress(lines: CountSessionLine[], drafts: Record<string, number | null>): { counted: number; total: number; differing: number } {
  let counted = 0
  let differing = 0
  for (const l of lines) {
    if (shownCount(l, drafts) == null) continue
    counted++
    const v = shownVariance(l, drafts)
    if (v != null && v !== 0) differing++
  }
  return { counted, total: lines.length, differing }
}

/** Case-insensitive name/code filter. */
export function filterLines(lines: CountSessionLine[], q: string): CountSessionLine[] {
  const s = q.trim().toLowerCase()
  if (!s) return lines
  return lines.filter((l) => l.item_name.toLowerCase().includes(s) || (l.code ?? '').toLowerCase().includes(s))
}

/** Read-only count view (Batch 6, manager): the counted lines in sheet order,
 *  or only those that differ from the book. A line not counted is left out —
 *  there is nothing to read on it. */
export function readOnlyCountLines(lines: CountSessionLine[], onlyDifferences: boolean): CountSessionLine[] {
  return lines
    .filter((l) => l.counted_qty != null && (!onlyDifferences || (l.variance != null && Math.abs(l.variance) >= 0.0005)))
    .sort((a, b) => a.position - b.position || a.item_name.localeCompare(b.item_name))
}
